import { useEffect, useState } from 'react';
import TermBlock, { TermSummaryLine } from './TermBlock.jsx';
import PendingTerm from './PendingTerm.jsx';

// THE TERMS: the current term (payload current_term_id, or ?term= from a
// search hit) open, every other term folded as one line. One tap opens an
// older term as the same block; fold closes it. Order is the payload's
// (newest effective first). A ?term= that arrives while the section is
// mounted (a palette hit on the account already open) opens that term and
// scrolls its block into view. Which term is current is the payload's
// current_term_id and nothing else (the contract names exactly one); the
// per-term flag is not read here, so the two cannot disagree on this page.
// PENDING terms (a deal that is only a session so far, payload pending_terms)
// come first and are always open; a ?term=pending:<session id> scrolls to one.

// A REMEDY THAT NAMES A ROW OF A FOLDED TERM OPENS THAT TERM (2026-10-06). Add it on the banner links to #fact-term-<term id>-<field>, the
// row of the term the banner is about; a folded term has no rows, so the link used to land on nothing and the broker typed the number into
// the current term's row instead. The hash names the term; this opens it (the same open set a ?term= uses), and the row's own input focuses
// itself when it mounts and the hash names it.
const ROW_HASH = /^#fact-term-(.+)-([a-z_]+)$/;
const termOfHash = (hash) => { const m = ROW_HASH.exec(hash || ''); return m ? m[1] : null; };

// How many open notes AIRgus holds on a term's own rows: a folded term shows them on its line, since its rows are not on the page.
function noteCount(byRef, termId) {
  if (!byRef) return 0;
  let n = 0;
  for (const [k, v] of byRef.entries()) if (k.startsWith(`term:${termId}:`)) n += v.length;
  return n;
}

export default function TermsSection({ terms = [], pending = [], currentTermId, focusTermId, byRef, ...rest }) {
  const isCurrent = (t) => t.id === currentTermId;
  const [open, setOpen] = useState(() => new Set(focusTermId ? [focusTermId] : []));
  const toggle = (id, on) => setOpen((s) => { const n = new Set(s); if (on) n.add(id); else n.delete(id); return n; });
  const [scrollTo, setScrollTo] = useState(focusTermId || null);
  useEffect(() => {
    if (!focusTermId) return;
    setOpen((s) => (s.has(focusTermId) ? s : new Set(s).add(focusTermId)));
    setScrollTo(focusTermId);
  }, [focusTermId]);
  useEffect(() => {
    const openNamed = () => {
      const id = termOfHash(window.location.hash);
      if (!id || !terms.some((t) => t.id === id)) return;
      setOpen((s) => (s.has(id) ? s : new Set(s).add(id)));
    };
    openNamed();
    window.addEventListener('hashchange', openNamed);
    return () => window.removeEventListener('hashchange', openNamed);
  }, [terms]);
  useEffect(() => {
    if (!scrollTo) return;
    const el = document.querySelector(`[data-term="${CSS.escape(scrollTo)}"]`);
    if (el) { el.scrollIntoView({ block: 'start', behavior: 'smooth' }); setScrollTo(null); }
  }, [scrollTo, open]);
  return (
    <section className="space-y-2" data-terms>
      <h2 className="text-[10px] font-semibold uppercase tracking-[0.14em] text-text-dim">Terms <span className="ml-1 font-mono normal-case tracking-normal">{terms.length}</span></h2>
      {terms.length === 0 && pending.length === 0 ? <p className="text-xs italic text-text-dim">No terms on this account.</p> : null}
      {pending.map((p) => <PendingTerm key={p.id} p={p} account={rest.ctx?.account} />)}
      {terms.map((t) => {
        const current = isCurrent(t);
        if (current || open.has(t.id)) return <TermBlock key={t.id} t={t} current={current} onFold={current ? null : () => toggle(t.id, false)} byRef={byRef} {...rest} />;
        return null;
      })}
      {terms.some((t) => !isCurrent(t) && !open.has(t.id)) ? (
        <div className="rounded-card border border-border bg-surface px-4" data-older-terms>
          {terms.filter((t) => !isCurrent(t) && !open.has(t.id)).map((t) => <TermSummaryLine key={t.id} t={t} notes={noteCount(byRef, t.id)} onOpen={() => toggle(t.id, true)} />)}
        </div>
      ) : null}
    </section>
  );
}
